import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import DashboardNavbar from "../components/layout/DashboardNavbar";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // named import — NOT side-effect

export default function WalletPayReceipt() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    status,
    paymentData,
    customerDetails,
    cart = [],
    totalAmount,
    chargeFee = 0,
  } = location.state || {};

  const [showReceiptModal, setShowReceiptModal] = useState(false);

  if (!status || !paymentData) {
    navigate("/wallet-pay");
    return null;
  }

  const isSuccess = status === "completed" || status === "success";
  const isPending = status === "pending";
  const isFailed = status === "failed";

  const totalCost =
    paymentData?.items?.reduce(
      (sum, item) => sum + Number(item.service.price) * item.qty,
      0
    ) ||
    totalAmount ||
    0;

  const grandTotal = totalCost + chargeFee;
  const reference = paymentData?.reference || paymentData?.invoice_reference || "N/A";
  const rrr = paymentData?.rrr || paymentData?.pay1one_ref || "—";
  const currentDate = new Date().toLocaleDateString("en-GB");
  const currentTime = new Date().toLocaleTimeString();

  // ─── Download PDF ─────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth(); // 210mm for A4

    // ₦ is outside Helvetica's Latin-1 charset → renders as "?" in jsPDF.
    // Use this helper for all monetary values in the PDF.
    const amt = (n) => `NGN ${Number(n).toLocaleString()}`;

    // ── Header ────────────────────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(0, 128, 0);
    doc.text("UCH SELF SERVICE", pageW / 2, 20, { align: "center" });

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Official Payment Receipt", pageW / 2, 28, { align: "center" });

    doc.setDrawColor(0, 128, 0);
    doc.line(20, 35, pageW - 20, 35);

    // ── Payment Status ────────────────────────────────────────────────────
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    if (isSuccess) doc.setTextColor(0, 128, 0);
    else if (isPending) doc.setTextColor(180, 130, 0);
    else doc.setTextColor(180, 0, 0);
    doc.text(
      isSuccess
        ? "Payment Successful"
        : isPending
        ? "Payment Pending"
        : "Payment Failed",
      pageW / 2,
      44,
      { align: "center" }
    );
    doc.setTextColor(0, 0, 0);

    // ── Patient Details ───────────────────────────────────────────────────
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("PATIENT INFORMATION", 20, 58);

    doc.setFont("helvetica", "normal");
    doc.text(
      `Name: ${customerDetails?.patient_first_name || ""} ${
        customerDetails?.patient_last_name || ""
      }`,
      20,
      65
    );
    doc.text(
      `Patient ID: ${customerDetails?.patient_hospital_number || "N/A"}`,
      20,
      72
    );

    // ── Payer Details ─────────────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.text("PAYER INFORMATION", 20, 85);

    doc.setFont("helvetica", "normal");
    doc.text(
      `Name: ${customerDetails?.payer_first_name || ""} ${
        customerDetails?.payer_last_name || ""
      }`,
      20,
      92
    );
    doc.text(`Email: ${customerDetails?.payer_email || "N/A"}`, 20, 99);
    doc.text(`Phone: ${customerDetails?.payer_phone_number || "N/A"}`, 20, 106);

    // ── Transaction Details ───────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.text("TRANSACTION DETAILS", 20, 119);

    doc.setFont("helvetica", "normal");
    doc.text(`Reference: ${reference}`, 20, 126);
    doc.text(`Date: ${currentDate} ${currentTime}`, 20, 133);
    doc.text(
      `Description: ${
        customerDetails?.transaction_description || "Wallet Payment"
      }`,
      20,
      140
    );

    // ── Services Table ────────────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.text("SERVICES PAID FOR", 20, 153);

    autoTable(doc, {
      startY: 160,
      head: [["Service", "Quantity", "Amount (NGN)"]],
      body: cart.map((item) => [
        item.name,
        item.quantity.toString(),
        amt(item.price * item.quantity),
      ]),
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: { fillColor: [0, 128, 0], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    // Guard against empty cart (lastAutoTable would be undefined)
    const finalY = (doc.lastAutoTable?.finalY ?? 160) + 10;

    // ── Payment Summary ───────────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.text("PAYMENT SUMMARY", 20, finalY + 10);

    doc.setFont("helvetica", "normal");
    doc.text("Subtotal:", 20, finalY + 20);
    doc.text(amt(totalCost), pageW - 20, finalY + 20, { align: "right" });

    doc.text("Charge Fee:", 20, finalY + 28);
    doc.text(amt(chargeFee), pageW - 20, finalY + 28, { align: "right" });

    doc.setDrawColor(0, 128, 0);
    doc.line(20, finalY + 35, pageW - 20, finalY + 35);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("TOTAL PAID", 20, finalY + 48);
    doc.text(amt(grandTotal), pageW - 20, finalY + 48, { align: "right" });

    // ── Footer ────────────────────────────────────────────────────────────
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(100, 100, 100);
    doc.text(
      "Thank you for choosing UCH Self Service",
      pageW / 2,
      finalY + 70,
      { align: "center" }
    );
    doc.text(
      "This is a computer-generated receipt and does not require a signature.",
      pageW / 2,
      finalY + 77,
      { align: "center" }
    );

    doc.save(`UCH_WalletReceipt_${reference}.pdf`);
  };

  // ─── Print Receipt ────────────────────────────────────────────────────────
  const handlePrintReceipt = () => {
    const printContent = document.getElementById("wallet-receipt-content");
    if (!printContent) return;
    const win = window.open("", "_blank", "width=700,height=900");
    win.document.write(`<!DOCTYPE html><html><head><title>UCH Wallet Receipt</title>
      <style>body{font-family:sans-serif;padding:24px;color:#111;}
      h3{color:#16a34a;} .green{color:#16a34a;} .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
      </style></head><body>${printContent.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-700 via-green-600 to-green-800 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_25%,rgba(255,255,255,0.1)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.1)_75%,rgba(255,255,255,0.1))] bg-[length:80px_80px]"></div>
      </div>

      <DashboardNavbar />

      <main className="relative z-10 max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mt-15">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-green-700 mb-10">
            Payment Receipt
          </h1>

          {/* Status Badge */}
          <div
            className={`text-center text-2xl md:text-3xl font-bold mb-10 ${
              isSuccess
                ? "text-green-600"
                : isPending
                ? "text-yellow-600"
                : "text-red-600"
            }`}
          >
            {isSuccess
              ? "Payment Successful!"
              : isPending
              ? "Payment Pending"
              : "Payment Failed"}
          </div>

          <div className="space-y-8">
            {/* Status Message */}
            <p className="text-center text-lg md:text-xl text-gray-700">
              {isSuccess
                ? "Your payment has been processed successfully."
                : isPending
                ? "Your payment is being processed. Please wait a moment."
                : paymentData?.message ||
                  "An error occurred during payment. Please try again."}
            </p>

            {/* Patient & Payer Info */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-green-50 p-6 rounded-2xl">
                <h3 className="text-xl font-bold text-green-700 mb-4">
                  Patient Details
                </h3>
                <p className="text-gray-700">
                  <strong>Name:</strong>{" "}
                  {customerDetails.patient_first_name}{" "}
                  {customerDetails.patient_last_name}
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Patient ID:</strong>{" "}
                  {customerDetails.patient_hospital_number || "N/A"}
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-2xl">
                <h3 className="text-xl font-bold text-green-700 mb-4">
                  Payer Details
                </h3>
                <p className="text-gray-700">
                  <strong>Name:</strong>{" "}
                  {customerDetails.payer_first_name}{" "}
                  {customerDetails.payer_last_name}
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Email:</strong> {customerDetails.payer_email}
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Phone:</strong> {customerDetails.payer_phone_number}
                </p>
              </div>
            </div>

            {/* Transaction Info */}
            <div className="bg-gray-50 p-6 rounded-2xl">
              <h3 className="text-xl font-bold text-green-700 mb-4">Transaction Details</h3>
              <div className="space-y-2.5">
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 font-medium text-sm">Transaction Reference</span>
                  <span className="font-bold text-[#0d3d2e] font-mono text-sm text-right">{reference}</span>
                </div>
                {rrr !== "—" && (
                  <div className="flex justify-between items-start">
                    <span className="text-gray-500 font-medium text-sm">RRR (Remita)</span>
                    <span className="font-bold text-[#0d3d2e] font-mono text-sm text-right">{rrr}</span>
                  </div>
                )}
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 font-medium text-sm">Payment Method</span>
                  <span className="text-gray-700 text-sm">Wallet Pay</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-500 font-medium text-sm">Description</span>
                  <span className="text-gray-700 text-sm text-right max-w-[60%]">{customerDetails.transaction_description || "Hospital Service Payment"}</span>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="bg-green-50 p-6 rounded-2xl">
              <h3 className="text-xl font-bold text-green-700 mb-4">
                Payment Summary
              </h3>

              {cart.length > 0 && (
                <div className="space-y-3 mb-6">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between text-gray-700"
                    >
                      <span>
                        {item.name} × {item.quantity}
                      </span>
                      <span>
                        ₦{(item.price * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3 pt-4 border-t border-green-200">
                <div className="flex justify-between text-lg">
                  <span>Subtotal</span>
                  <span>₦{totalCost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Charge Fee</span>
                  <span>₦{chargeFee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-2xl font-bold text-green-700 pt-4">
                  <span>Total Paid</span>
                  <span>₦{grandTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps — only for successful payments */}
          {isSuccess && (
            <div className="mt-8 bg-green-50 border border-green-200 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-green-700 mb-5 flex items-center gap-2">
                📋 Next Steps to Receive Your Service
              </h3>
              <div className="space-y-4">
                {[
                  {
                    title: "Proceed to the Payment Booth",
                    desc: "Visit any UCH Pay1One payment booth located within the hospital premises.",
                  },
                  {
                    title: "Inform the Teller",
                    desc: `Tell the teller you are a VIP Payer and present your Transaction Reference: ${reference}. This code is for one-time use only.`,
                  },
                  {
                    title: "Collect Your Official Receipt",
                    desc: "The teller will verify your payment on the system and issue you an official hospital receipt.",
                  },
                  {
                    title: "Proceed to Your Service Point",
                    desc: "With your official receipt, go to the relevant department or ward to receive your hospital service.",
                  },
                ].map((step, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{step.title}</p>
                      <p className="text-gray-600 text-sm mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-green-700 border-t border-green-200 pt-3">
                📧 A confirmation email with your receipt and these steps has been sent to <strong>{customerDetails?.payer_email}</strong>
              </p>
              <div className="mt-4 border-t border-green-200 pt-4">
                <p className="text-sm text-green-700 mb-2 font-medium">How was your experience?</p>
                <button
                  onClick={() => navigate("/feedback")}
                  className="inline-flex items-center gap-2 bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-green-700 transition"
                >
                  💬 Give Feedback
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            {/* View Receipt — only meaningful on success */}
            {isSuccess && (
              <Button
                onClick={() => setShowReceiptModal(true)}
                className="bg-white border-2 border-green-600 text-green-700 hover:bg-green-50 font-bold py-4 px-8 rounded-xl text-lg"
              >
                📄 View Receipt
              </Button>
            )}

            {(isPending || isFailed) && (
              <Button
                onClick={() => navigate("/wallet-pay")}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-4 px-10 rounded-xl text-lg"
              >
                Try Again
              </Button>
            )}

            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-10 rounded-xl text-lg"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </main>

      {/* ==================== RECEIPT MODAL ==================== */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">

            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-green-700">
                Payment Receipt
              </h2>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleDownloadPDF}
                  className="px-5 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition flex items-center gap-2"
                >
                  ↓ Download PDF
                </button>
                <button
                  onClick={() => setShowReceiptModal(false)}
                  className="px-5 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Receipt Content — Printable Area */}
            <div
              id="wallet-receipt-content"
              className="p-8 overflow-auto flex-1 text-sm"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-green-700">
                  UCH Self Service
                </h3>
                <p className="text-gray-500 mt-1">Official Payment Receipt</p>
                <div className="h-px bg-gray-300 my-6"></div>
              </div>

              {/* Status line inside receipt */}
              <div
                className={`text-center font-bold text-lg mb-6 ${
                  isSuccess
                    ? "text-green-600"
                    : isPending
                    ? "text-yellow-600"
                    : "text-red-600"
                }`}
              >
                {isSuccess
                  ? "✓ Payment Successful"
                  : isPending
                  ? "⏳ Payment Pending"
                  : "✗ Payment Failed"}
              </div>

              <div className="space-y-6">
                {/* Patient Info */}
                <div>
                  <p className="font-semibold text-gray-700 mb-2">
                    PATIENT INFORMATION
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                    <p>Name:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.patient_first_name}{" "}
                      {customerDetails?.patient_last_name}
                    </p>
                    <p>Patient ID:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.patient_hospital_number || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Payer Info */}
                <div>
                  <p className="font-semibold text-gray-700 mb-2">
                    PAYER INFORMATION
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                    <p>Name:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.payer_first_name}{" "}
                      {customerDetails?.payer_last_name}
                    </p>
                    <p>Email:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.payer_email || "N/A"}
                    </p>
                    <p>Phone:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.payer_phone_number || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Transaction Info */}
                <div>
                  <p className="font-semibold text-gray-700 mb-2">
                    TRANSACTION DETAILS
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                    <p>Reference:</p>
                    <p className="font-medium text-right font-mono">
                      {reference}
                    </p>
                    <p>Date:</p>
                    <p className="font-medium text-right">
                      {currentDate} {currentTime}
                    </p>
                    <p>Description:</p>
                    <p className="font-medium text-right">
                      {customerDetails?.transaction_description ||
                        "Wallet Payment"}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="font-semibold text-gray-700 mb-3">
                    SERVICES PAID FOR
                  </p>
                  <div className="border rounded-xl overflow-hidden">
                    {cart.length > 0 ? (
                      cart.map((item, index) => (
                        <div
                          key={item.id || index}
                          className="flex justify-between px-4 py-3 border-b last:border-b-0 bg-gray-50"
                        >
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-xs text-gray-500">
                              Qty: {item.quantity}
                            </p>
                          </div>
                          <p className="font-medium">
                            ₦{(item.price * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="px-4 py-3 text-gray-500 italic">
                        No items found
                      </p>
                    )}
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-100 rounded-xl p-5 space-y-3">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span>
                    <span>₦{totalCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Charge Fee</span>
                    <span>₦{chargeFee.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-3 flex justify-between text-lg font-bold text-green-700">
                    <span>TOTAL PAID</span>
                    <span>₦{grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="text-center mt-10 text-gray-400 text-xs">
                Thank you for your payment • This is a computer-generated receipt
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}